import React, { createContext, useState, useEffect } from 'react';
import { useBusinessAccounts, BusinessAccount } from '@/hooks/useBusinessAccounts';

export interface BusinessAccountContextType {
  selectedAccountId: string | null;
  selectedAccount: BusinessAccount | null;
  setSelectedAccountId: (accountId: string) => void;
  clearSelectedAccount: () => void;
  setWorkspaceAccountOverride: (accountId: string | null) => void;
  accounts: BusinessAccount[] | null;
  isLoading: boolean;
}

const isValidStoredId = (v: string | null) => typeof v === 'string' && v.trim().length > 0;

const globalKey = '__BUSINESS_ACCOUNT_CONTEXT__';
const globalObject = typeof globalThis !== 'undefined' ? (globalThis as any) : {};

export const BusinessAccountContext: React.Context<BusinessAccountContextType | undefined> =
  globalObject[globalKey] || createContext<BusinessAccountContextType | undefined>(undefined);

if (globalObject && !globalObject[globalKey]) {
  globalObject[globalKey] = BusinessAccountContext;
}

export const BusinessAccountProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: accounts, isLoading, isPending } = useBusinessAccounts();
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('selectedBusinessAccountId');
      if (isValidStoredId(stored)) return stored;
    }
    return null;
  });

  useEffect(() => {
    if (selectedAccountId && typeof window !== 'undefined') {
      localStorage.setItem('selectedBusinessAccountId', selectedAccountId);
    }
  }, [selectedAccountId]);

  const [workspaceOverride, setWorkspaceOverride] = useState<{ active: boolean; accountId: string | null }>(
    { active: false, accountId: null },
  );
  const setWorkspaceAccountOverride = React.useCallback((accountId: string | null) => {
    setWorkspaceOverride({ active: true, accountId });
  }, []);

  useEffect(() => {
    if (workspaceOverride.active) return;
    if (accounts && accounts.length > 0 && !selectedAccountId) {
      setSelectedAccountId(accounts[0].id);
    }
  }, [accounts, selectedAccountId, setSelectedAccountId, workspaceOverride.active]);

  useEffect(() => {
  }, [accounts, selectedAccountId]);

  const resolvedAccountId = workspaceOverride.active
    ? workspaceOverride.accountId
    : (selectedAccountId ?? accounts?.[0]?.id ?? null);

  const selectedAccount = accounts?.find(account => account.id === resolvedAccountId) || null;

  const clearSelectedAccount = () => {
    setSelectedAccountId(null);
    if (typeof window !== 'undefined') {
      localStorage.removeItem('selectedBusinessAccountId');
    }
  };

  const value: BusinessAccountContextType = {
    selectedAccountId: resolvedAccountId,
    selectedAccount,
    setSelectedAccountId,
    clearSelectedAccount,
    setWorkspaceAccountOverride,
    accounts: accounts || null,
    isLoading: Boolean(isPending) || isLoading,
  };

  return (
    <BusinessAccountContext.Provider value={value}>
      {children}
    </BusinessAccountContext.Provider>
  );
};

export default BusinessAccountProvider;
